import React, { useState } from "react";
import {
  Box,
  Button,
  Collapse,
  Paper,
  Stack,
  Typography,
  Switch,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Chip,
  CircularProgress,
} from "@mui/material";
import PrintIcon from "@mui/icons-material/Print";
import EditIcon from "@mui/icons-material/Edit";
import { useParams } from "react-router-dom";
import { useQuery } from "react-query";
import useClientDetails from "../../../../../hooks/useClientDetails";
import { getRisks } from "../../../../../api/services/riskProfile";
import { QK } from "../../../../../base/qk";
import RiskDialog from "../ActiveCareTabs/CarePlan/Dialog/RiskFormDialog";

const getRiskStatus = (impact, likelihood) => {
  const score = (Number(impact) || 0) * (Number(likelihood) || 0);
  if (score >= 15) return { label: "EXTREME", color: "#e11d48", bg: "#fff1f2", score };
  if (score >= 8) return { label: "HIGH", color: "#f59e0b", bg: "#fffbeb", score };
  if (score >= 4) return { label: "MEDIUM", color: "#3b82f6", bg: "#eff6ff", score };
  if (score >= 1) return { label: "LOW", color: "#10b981", bg: "#ecfdf5", score };
  return { label: "N/A", color: "#94a3b8", bg: "#f8fafc", score };
};

const RiskProfileTabs = () => {
  const [showData, setShowData] = useState(false);
  const [showDeleted, setShowDeleted] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingRisk, setEditingRisk] = useState(null);

  const { id: clientID } = useParams(); 
  const { fullName } = useClientDetails(clientID);

  const { data: risks, isLoading } = useQuery(
    [QK.risks, clientID], 
    () => getRisks(clientID),
    { 
      enabled: !!clientID && showData, 
      refetchOnWindowFocus: false 
    }
  );

  const handleEdit = (risk) => {
    setEditingRisk(risk);
    setOpenDialog(true);
  };

  const handleShowClick = () => setShowData(!showData);

  const ScoreChip = ({ impact, likelihood, isTarget = false }) => {
    const status = getRiskStatus(impact, likelihood);
    return (
      <Chip
        label={`${status.label} (${status.score})`}
        size="small"
        sx={{
          fontWeight: 800,
          fontSize: "0.65rem",
          bgcolor: status.bg,
          color: status.color,
          border: isTarget ? `1px dashed ${status.color}` : `1px solid ${status.color}30`,
          minWidth: '85px'
        }}
      />
    );
  };

  return (
    <Box sx={{ width: "100%", mb: 3 }}>
      <Button
        fullWidth
        onClick={handleShowClick}
        variant="contained"
        sx={{
          justifyContent: "space-between",
          py: 1.8,
          px: 2.5,
          borderRadius: 2,
          fontWeight: 600,
          textTransform: "none",
          mb: 2,
          background: showData
            ? "linear-gradient(90deg, #0288d1, #03a9f4)"
            : "linear-gradient(90deg, #1976d2, #42a5f5)",
          color: "white",
          "&:hover": {
            background: "linear-gradient(90deg, #1565c0, #29b6f6)",
          },
        }}
      >
        Risk Assessment for {fullName}
        <Typography variant="caption" sx={{ ml: 2, opacity: 0.8 }}>
          {showData ? "Collapse" : "Expand"}
        </Typography>
      </Button>

      <Collapse in={showData}>
        <Paper
          elevation={2}
          sx={{
            p: 2,
            borderRadius: 2,
            bgcolor: "#fafafa",
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={1}>
            <IconButton color="error" size="small">
              <PrintIcon fontSize="small" />
            </IconButton>
            <Typography variant="body2" color="text.secondary">Show Deleted</Typography>
            <Switch
              checked={showDeleted}
              onChange={(e) => setShowDeleted(e.target.checked)}
              color="primary"
            />
          </Stack>

          <TableContainer sx={{ maxHeight: 500, border: '1px solid #e0e0e0', borderRadius: 1 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 800, bgcolor: "#f8fafc" }}>Risk Name</TableCell>
                  <TableCell sx={{ fontWeight: 800, bgcolor: "#f8fafc" }}>Care Plan</TableCell>
                  <TableCell sx={{ fontWeight: 800, bgcolor: "#f8fafc" }} align="center">Initial</TableCell>
                  <TableCell sx={{ fontWeight: 800, bgcolor: "#f8fafc" }} align="center">Latest</TableCell>
                  <TableCell sx={{ fontWeight: 800, bgcolor: "#f8fafc" }} align="center">Target</TableCell>
                  <TableCell sx={{ fontWeight: 800, bgcolor: "#f8fafc" }} align="center">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      <CircularProgress size={24} />
                    </TableCell>
                  </TableRow>
                ) : risks?.results?.length > 0 ? (
                  risks.results.map((risk) => (
                    <TableRow key={risk.id} hover>
                      <TableCell sx={{ fontWeight: 600 }}>{risk.risk_name}</TableCell>
                      <TableCell>
                        <Typography variant="body2" color="primary" sx={{ fontWeight: 500 }}>
                          {risk.care_plan_name || "N/A"}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <ScoreChip impact={risk.initial_impact} likelihood={risk.initial_likelihood} />
                      </TableCell>
                      <TableCell align="center">
                        <ScoreChip impact={risk.latest_impact} likelihood={risk.latest_likelihood} />
                      </TableCell>
                      <TableCell align="center">
                        <ScoreChip impact={risk.target_impact} likelihood={risk.target_likelihood} isTarget />
                      </TableCell>
                      <TableCell align="center">
                        <IconButton 
                          size="small" 
                          sx={{ color: '#1976d2' }}
                          onClick={() => handleEdit(risk)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                      No risk assessments found for this client.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Collapse>

      <RiskDialog
        open={openDialog}
        handleClose={() => {
          setOpenDialog(false);
          setEditingRisk(null);
        }}
        editingRisk={editingRisk}
      />
    </Box>
  );
};

export default RiskProfileTabs;